// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import productDescriptionVariantStyles from "@flip/App/SharedStyle/ProductDescriptionStyles";
import { constants } from "@flip/XDL/constants";
import Close from "@flip/XDL/Icons/Close";
import SaveOrder from "@flip/XDL/Icons/SaveOrder";
import {
    avoidBreakInsidePrintElement,
    color,
    createLinkIconStyles,
    ellipsis,
    mergeWithCss,
    xScreen,
} from "@flip/XDL/Utilities";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { IconWrapper } from "@insite/mobius/Icon";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

interface CartPageStyles {
    header?: {
        wrapper?: InjectableCss;
        title?: TypographyPresentationProps;
    };
    cartActions?: {
        saveOrderLink?: LinkPresentationProps;
        addAllToListClickable?: LinkPresentationProps;
        removeAll?: LinkPresentationProps;
        requestQuote?: LinkPresentationProps;
    };
    cartLine?: {
        productImage?: ProductImageStyles;
        productBrand?: ProductBrandStyles;
        productDescription?: ProductDescriptionStyles;
        productPartNumbers?: ProductPartNumbersStyles;
        productAvailability?: ProductAvailabilityStyles;
        productPrice?: ProductPriceStyles;
        quantity?: CartLineQuantityStyles;
        totalSmallHeadingAndText?: SmallHeadingAndTextStyles;
        cartLineNotes?: CartLineNotesStyles;
        addToListLink?: LinkPresentationProps;
        removeLink?: LinkPresentationProps;
        totalSmallHeadingAndTextTable?: SmallHeadingAndTextStyles;
        qtyLabel?: TypographyProps;
        totalPriceContainer?: TypographyProps;
        extendedUnitNetPriceText?: SmallHeadingAndTextStyles;
        priceHeadingAndText?: SmallHeadingAndTextStyles;
        quantityHeadingAndText?: SmallHeadingAndTextStyles;
        productLazyImage?: LazyImageProps;
    };
    cartTotalWrapper?: InjectableCss;
}
const linkIconStyles: LinkPresentationProps = {
    typographyProps: {
        weight: 500,
        css: css`
            letter-spacing: 0;
            line-height: 16px;
        `,
    },
    icon: {
        iconProps: {
            size: 8,
        },
    },
};

const cartPageStyles: CartPageStyles = {
    cartActions: {
        saveOrderLink: {
            ...createLinkIconStyles(SaveOrder, {
                ...linkIconStyles,
                icon: {
                    iconProps: {
                        size: 11,
                    },
                },
            }),
        },
        removeAll: {
            ...createLinkIconStyles(Close, linkIconStyles),
        },
    },
    cartLine: {
        productImage: {
            linkWrappingImage: {
                css: css`
                    width: 91px;
                    height: 91px;
                    background-color: ${color("common.white")};
                    justify-content: center;
                    &&& > img {
                        max-width: 100%;
                        max-height: 100%;
                        height: auto;
                        object-fit: contain;
                    }
                `,
            },
        },
        productLazyImage: {
            imgProps: {
                style: {
                    width: 91,
                    height: 91,
                    objectFit: "contain",
                },
            },
        },
        productDescription: {
            ...mergeWithCss(productDescriptionVariantStyles.small, {
                productDetailDescription: {
                    css: css`
                        ${xScreen("lte", "md")} {
                            padding-right: 12px;
                        }

                        ${ellipsis(2)}
                    `,
                },
                productDetailLink: {
                    typographyProps: {
                        css: css`
                            ${ellipsis(2)}
                        `,
                    },
                    css: css`
                        ${xScreen("lte", "md")} {
                            padding-right: 12px;
                        }
                        margin-bottom: 10px;
                        white-space: pre-wrap;
                    `,
                },
            }),
        },
        quantity: {},
        removeLink: {
            ...createLinkIconStyles(Close, {
                icon: {
                    iconProps: {
                        size: 10,
                        color: "text",
                    },
                },
                css: css`
                    ${IconWrapper} {
                        opacity: 1;
                        margin: 0;
                    }
                `,
            }),
        },
        productPrice: {
            price: {
                priceText: {
                    variant: "h6",
                },
            },
            priceLabelText: {
                variant: "legend",
                css: css`
                    &&& {
                        margin-bottom: 0;
                    }

                    ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                        display: none;
                    }
                `,
            },
        },
        productAvailability: {
            container: {
                css: css`
                    margin-bottom: 12px;
                `,
            },
        },
        totalSmallHeadingAndText: {
            text: {
                variant: "h6",
                css: css`
                    margin-bottom: 0;
                `,
            },
            heading: {
                variant: "legend",
                css: css`
                    ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                        display: none;
                    }
                `,
            },
        },
        totalSmallHeadingAndTextTable: {
            text: {
                variant: "h6",
            },
            heading: {
                css: css`
                    display: none;
                `,
            },
        },
        qtyLabel: {
            as: "label",
            size: 12,
            weight: "normal",
            lineHeight: "13px",
            css: css`
                margin-bottom: 8px;
                display: block;
                ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                    display: none;
                }
            `,
        },
        totalPriceContainer: {
            css: css`
                flex-direction: column;
                align-items: center;
                ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                    padding-right: 16px;
                    text-align: right;
                }
            `,
        },
        extendedUnitNetPriceText: {
            extendedStyles: {
                heading: {
                    variant: "legend",
                    css: css`
                        ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                            display: none;
                        }
                    `,
                },
                text: {
                    variant: "h6",
                },
            },
        },
    },
    header: {
        wrapper: {
            css: css`
                margin-bottom: 20px;
                display: flex;
                flex-direction: column;
                border-top: 1px solid ${color("common.border")};
                padding-top: 8px;
                ${avoidBreakInsidePrintElement}
            `,
        },
        title: {
            variant: "h4",
            css: css`
                line-height: 20px;
                margin-bottom: 0;
            `,
        },
    },
    cartTotalWrapper: {
        css: css`
            border: 1px solid ${color("common.border")};
            border-radius: 8px;
            padding: 16px;
        `,
    },
};

export default cartPageStyles;
